// @ts-check
// PURE offer/gallery helpers — the single source of truth for "which image does a
// product-checkout page actually paint, and in what order".
//
// Extracted from hooks/useProductOffers (task #191): that hook imports React +
// Overmind at module scope, so the SERVER-ONLY LCP resolver (lib/lcpImage) could
// not reuse its logic and instead hand-rolled its own `itemMainImage` — which
// drifted (it prioritised `imageOverride`, the shop-GRID thumbnail, while the
// carousel paints `galleryImages[0]`), so the LCP <link rel=preload> targeted a
// DIFFERENT image than the gallery rendered → a wasted preload + the real LCP
// image discovered late (#180-class). Both the client hook and the server resolver
// now import THESE functions, so the preload can never disagree with the paint.
//
// Pure: imports only pure utils (no React/Overmind/manifest), so it is safe to
// import from the server-only data layer AND the client hook.
/** @typedef {import('./schemas').ResolvedImage} ResolvedImage */
import { findItemByType, isUnavailableInRegion } from '../utils/shopItems'
import { calculatePriceForMultipleShopItems } from '../utils/shopItemsAndCart'
import { toImageEntry } from '../utils/imageSrc'

/**
 * Pick the page's main item, PREFERRING a variant that actually has a price in the
 * resolved region (the catalog is global but the backend is per-region, so a type
 * can match two docs where only one is priced — e.g. the R295 G2 vs its legacy-wheel
 * twin). `findItemByType` matches on TYPE ALONE, so a naive first-match can grab the
 * priceless variant — which has DIFFERENT gallery images than the one the page
 * actually renders. Both the page (useProductOffers) and the LCP resolver
 * (lib/lcpImage) resolve the item through THIS function so they agree on which
 * item — and therefore which hero image — the page shows.
 *
 * @param {any} itemType
 * @param {any} fallbackItemType
 * @param {Array<any>} shopItems
 * @returns {any|null}
 */
export function resolveProductItem(itemType, fallbackItemType, shopItems) {
  const items = shopItems || []
  const priced = (type) => {
    if (type == null) return null
    const found = findItemByType(type, items)
    return found && !isUnavailableInRegion(found) ? found : null
  }
  return (
    priced(itemType) ||
    priced(fallbackItemType) ||
    findItemByType(itemType, items) ||
    (fallbackItemType != null ? findItemByType(fallbackItemType, items) : undefined) ||
    null
  )
}

/**
 * Derive the gallery image entries for a selected offer, in PAINT ORDER (entry 0
 * is the image the gallery/carousel shows first). Entries are returned BARE (the
 * ResolvedImage descriptors / {src}), never double-wrapped. Mirrors exactly what
 * ProductCheckoutPage renders: getOfferImages(offers[selectedOfferIndex], item).
 *
 * @param {Array<any>|null|undefined} offer  the selected offer (array of items)
 * @param {any} mainItem                      the page's main item (fallback source)
 * @returns {Array<ResolvedImage|{src: string}>} normalized, uncapped gallery entries
 */
export function getOfferImages(offer, mainItem) {
  const pick = (...candidates) => {
    for (const c of candidates) {
      if (Array.isArray(c)) { if (c.length) return c }
      else if (c) { const e = toImageEntry(c); if (e) return [e] }
    }
    return []
  }

  if (!offer || !offer.length) {
    return pick(mainItem && mainItem.galleryImages, mainItem && mainItem.imageUrl)
  }

  if (offer.length > 1) {
    // more than one item => it's a set/bundle
    return pick(offer[0].fullSetGalleryImages, offer[0].galleryImages, offer[0].fullSetImage, offer[0].imageUrl)
  }

  return pick(offer[0].galleryImages, offer[0].imageUrl)
}

/**
 * Build the ordered list of offers for a main item and its product set.
 *
 * Excludes the main item by its SCALAR `type` (never the itemType OBJECT, whose
 * reference identity differs between the SSR and client Overmind instances → a
 * duplicate offer card → React #418 hydration mismatch; see task #23).
 *
 * @param {any} mainItem
 * @param {Array<any>} shopItems
 * @param {Object} [opts]
 * @param {boolean} [opts.showOptionWithAllItemsInSet] prepend a combined "all items in set" offer
 * @param {boolean} [opts.requireDisplayOnShop]        only include shop-visible items (ProductCheckoutPage rule)
 * @param {boolean} [opts.sortByPriceDescending]       sort offers by price, high → low
 * @returns {Array<Array<any>>} offers (each offer is an array of items)
 */
export function getItemOffers(mainItem, shopItems, opts = {}) {
  const { showOptionWithAllItemsInSet, requireDisplayOnShop, sortByPriceDescending } = opts

  let sameSetItems = shopItems
    .filter(i => i.type !== mainItem.type)
    .filter(i => i.productSetId === mainItem.productSetId)
    // Drop set siblings that carry NO PRICE in the resolved region. A product set can hold
    // two regional variants of the same thing — the R295 G2 bundle and its legacy-wheel twin
    // share a productSetId — and the catalog is global, so the variant this region's backend
    // did NOT return is still here with price=null (regionData stamps UNAVAILABLE + nulls it).
    .filter(i => !isUnavailableInRegion(i))
  if (requireDisplayOnShop) {
    sameSetItems = sameSetItems.filter(i => i.displayOnShop)
  }

  const offers = []

  if (requireDisplayOnShop) {
    // ProductCheckoutPage: only surface the main item (and the combined offer)
    // when it is visible on the shop.
    showOptionWithAllItemsInSet && mainItem.displayOnShop && sameSetItems && sameSetItems.length && offers.push([mainItem, ...sameSetItems])
    mainItem.displayOnShop && offers.push(Array.of(mainItem))
  } else {
    showOptionWithAllItemsInSet && sameSetItems && sameSetItems.length && offers.push([mainItem, ...sameSetItems])
    offers.push(Array.of(mainItem))
  }
  sameSetItems && sameSetItems.length && sameSetItems.forEach(i => offers.push(Array.of(i)))

  if (sortByPriceDescending) {
    offers.sort((a, b) => {
      const priceA = calculatePriceForMultipleShopItems(a)
      const priceB = calculatePriceForMultipleShopItems(b)
      return (priceB ? priceB.priceCents : 0) - (priceA ? priceA.priceCents : 0)
    })
  }

  return offers
}
